<template>
  <div ref="debug" class="debug">
    <div class="top">
      <div class="left">
        <div class="title">
          Debugger
        </div>
        <div v-if="getEntriesNumber > 1" class="undo">
          <button class="button" @click="onClickBack">
            <svg-icon
              :width="'14'"
              :height="'14'"
              fill="#db841e"
              icon="app-icons/undo"
            />
            Undo last choice
          </button>
        </div>
      </div>
      <div class="close" @click="onCloseDebug">
        <svg-icon :width="'14'" :height="'14'" icon="app-icons/close" />
      </div>
    </div>
    <div class="variables-title" @click="onClickTitle">
      <div class="arrow">
        <svg-icon
          :width="'12'"
          :height="'12'"
          :icon="isCollapsed ? 'app-icons/arrow-right' : 'app-icons/arrow-down'"
        />
      </div>
      <div class="title-smaller">
        {{ variableRows.length }}
        {{ variableRows.length === 1 ? 'global variable' : 'global variables' }}
      </div>
    </div>
    <!-- The variables grid -->
    <section v-show="!isCollapsed" class="vars">
      <!-- Grid headers -->
      <div class="row">
        <span>
          <strong>Name</strong>
        </span>
        <span style="margin-left:10px">
          <strong
            v-tooltip="{
              ...tooltipOptions,
              content: 'Before any scripting <br>is run on the page',
              html: true,
            }"
            >Before</strong
          >
        </span>
        <span style="margin-left:10px">
          <strong
            v-tooltip="{
              ...tooltipOptions,
              content: 'After any scripting <br>is run on the page',
              html: true,
            }"
            >After</strong
          >
        </span>
      </div>
      <!-- Grid data -->
      <prototype-debug-console-row
        v-for="v in variableRows"
        :id="v.id"
        :key="v.id"
        :name="v.name"
        :initial-before="v.initialBefore"
        :before="v.before"
        :after="v.after"
        :type="v.type"
        @updateBeforeValue="onUpdateBeforeValue"
      />
    </section>
  </div>
</template>

<script>
import {
  OverlayScrollbars,
  ScrollbarsHidingPlugin,
  SizeObserverPlugin,
} from 'overlayscrollbars';
import { mapGetters, mapMutations } from 'vuex';
import _ from 'lodash';
import PrototypeDebugConsoleRow from './PrototypeDebugConsoleRow.vue';
import SvgIcon from '../all/SvgIcon.vue';

/**
 * Our constant tooltip options
 */
const TOOLTIP_OPTIONS = {
  placement: 'top-start',
  distance: 10,
  boundary: document.body,
  color: 'primary',
  popperClass: ['secondary'],
};

export default {
  components: {
    SvgIcon,
    PrototypeDebugConsoleRow,
  },

  props: {
    /**
     * The variable initial values (before the rendering)
     */
    initialBeforeValues: {
      type: Object,
      default: null,
    },

    /**
     * The variable initial values (before the rendering), merged with user-defined changes
     */
    beforeValues: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      isCollapsed: false,
      osInstance: null,
    };
  },

  computed: {
    ...mapGetters('playMode', ['getValue', 'getEntriesNumber']),
    ...mapGetters('project/data/variables', [
      'getContainedNodes',
      'getRootContainer',
    ]),

    /**
     * Return all the project variables declarations with their before and after values
     * @return {array}
     */
    variableRows() {
      console.log('Recalculating variable rows');
      if (!this.getRootContainer) return [];

      const { id } = this.getRootContainer;
      const vars = this.getContainedNodes(id, 'variables', 'children');
      console.log('vars:', vars);
      console.log('beforeValues:', _.cloneDeep(this.beforeValues));
      return vars.map(v => ({
        ...v,
        initialBefore: this.initialBeforeValues[v.id],
        before: this.beforeValues[v.id],
        after: this.getValue(v.id),
      }));
    },

    /**
     * The tooltip options for the v-tooltip component
     * @returns {Object}
     */
    tooltipOptions() {
      return {
        // The dynamic content of the tooltip
        content: this.type,

        // The constant tooltip options
        ...TOOLTIP_OPTIONS,
      };
    },
  },

  /**
   * Vue lifecycle hook
   */
  mounted() {
    OverlayScrollbars.plugin([ScrollbarsHidingPlugin, SizeObserverPlugin]);
    this.osInstance = OverlayScrollbars(this.$refs.debug, {
      scrollbars: {
        autoHide: 'move',
        autoHideDelay: 600,
      },
    });
    $(this.$el).resizable({
      minWidth: 340,
      maxWidth: 680,
      handles: 'w',
    });
  },

  methods: {
    ...mapMutations('application', ['setDebugSection']),

    /**
     * Event handler: The user closes the debug console
     */
    onCloseDebug() {
      this.setDebugSection({ value: false });
    },

    /**
     * Event handler: the user clicks on title. Collapse or expand the debug console.
     */
    onClickTitle() {
      this.isCollapsed = !this.isCollapsed;
    },

    /**
     * Event handler: the user clicked on the back button.
     */
    onClickBack() {
      this.$emit('clickBack');
    },

    /**
     * Event hanlder for custom 'updateBeforeValue' event
     * @param {string} id The variable id
     * @param {mixed} value The updated value
     */
    onUpdateBeforeValue({ id, value }) {
      this.$emit('updateBeforeValue', { id, value });
    },
  },
};
</script>
<style scoped lang="scss">
.vars {
  .row {
    display: inline-grid;
    grid-template-columns: repeat(3, 0.7fr);
    padding: 0 12px 5px;
    width: 100%;
    span {
      padding: 24px 10px;
      border-bottom: 1px solid #2c3131;
      &:nth-child(-n + 3) {
        border-bottom: none;
        padding: 14px 8px 8px;
      }
      &:nth-child(1n + 0) {
        width: 100%;
      }
      &:nth-last-child(-n + 3) {
        border-bottom: none;
      }
      strong {
        text-transform: uppercase;
        font-size: 13px;
        font-weight: 600;
        opacity: 0.5;
      }
      // &:nth-child(3n + 0) {
      //   text-align: right;
      // }
    }
  }
}

.debug {
  left: auto !important;
  color: #d6d6d6;
  padding: 50px 10px 10px;
  border-left: 1px solid #2c3131;
  font-family: 'Open Sans';
  font-size: 14px;
  overflow-y: auto;
  background: #000;
  width: 460px;
  position: relative;
  height: 100%;

  .top {
    display: flex;
    align-items: center;
    padding: 0 0 0 20px;
    .left {
      flex: 1;
      display: flex;
      align-items: center;
      .title {
        font-weight: 300;
        font-size: 24px;
      }

      .undo {
        .button {
          display: flex;
          color: #fff;
          border-radius: 3px;
          padding: 10px 12px;
          color: #db841e;
          // background: rgba(37, 41, 41, 0.8);
          background: none;
          cursor: pointer;
          border: none;
          margin-left: 20px;
          margin-top: 5px;
          .svg-icon-container {
            margin: 0 6px 0 0;
          }
          &:hover {
            // background: #ce750d;
            background: rgba(37, 41, 41, 1);
          }
        }
      }
    }
    .close {
      cursor: pointer;
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
  }
  .variables-title {
    display: flex;
    align-items: center;
    padding: 0 0 0 20px;
    margin: 30px 0 15px 0;
    cursor: pointer;
    user-select: none;
    .title-smaller {
      font-weight: 300;
      font-size: 16px;
    }
    .arrow {
      margin-right: 10px;
    }
  }
  .grid {
    display: inline-grid;
    grid-template-columns: repeat(3, 0.7fr);
    padding: 0 12px 5px;
    max-height: auto;
    overflow-y: auto;
    width: 100%;
    > span {
      padding: 24px 10px;
      border-bottom: 1px solid #2c3131;
      &:nth-child(-n + 3) {
        border-bottom: none;
        padding: 14px 8px 8px;
      }
      &:nth-child(1n + 0) {
        width: 100%;
      }
      &:nth-last-child(-n + 3) {
        border-bottom: none;
      }
      strong {
        text-transform: uppercase;
        font-size: 13px;
        font-weight: 600;
        opacity: 0.5;
      }
      &:nth-child(3n + 0) {
        text-align: right;
      }
    }
  }
}
</style>

<style lang="scss">
.debug {
  .os-scrollbar {
    --os-handle-bg: #242829;
    --os-handle-bg-hover: #424a4c;
    --os-handle-bg-active: #424a4c;
  }
}
.debug .ui-resizable-handle {
  position: absolute;
  font-size: 0.1px;
  opacity: 0;
  -ms-touch-action: none;
  touch-action: none;
  z-index: auto !important;
  background: #fff;
}

.debug .ui-resizable-handle:hover,
.debug .ui-resizable-resizing .ui-resizable-handle {
  opacity: 0.1;
}

.debug .ui-resizable-w {
  cursor: e-resize;
  height: 100%;
  width: 4px;
  top: 0;
  left: 0;
  opacity: 0 !important;
}
</style>
